import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Shield, Award, AlertTriangle } from "lucide-react";

export default function HealthScore({ bloodTests }) {
  const calculateHealthScore = () => {
    if (!bloodTests || bloodTests.length === 0) return 0;

    const latestTest = bloodTests[0];
    if (!latestTest?.results) return 0;

    let totalScore = 0;
    let categoryCount = 0;

    // Glucose scoring (0-100)
    if (latestTest.results.glucose) {
      const glucose = latestTest.results.glucose;
      let glucoseScore = 100;
      if (glucose > 100) glucoseScore = Math.max(0, 100 - (glucose - 100) * 2);
      else if (glucose < 70)
        glucoseScore = Math.max(0, 100 - (70 - glucose) * 3);
      totalScore += glucoseScore;
      categoryCount++;
    }

    // Cholesterol scoring (0-100)
    if (latestTest.results.cholesterol_total) {
      const cholesterol = latestTest.results.cholesterol_total;
      let cholesterolScore = 100;
      if (cholesterol > 200)
        cholesterolScore = Math.max(0, 100 - (cholesterol - 200) * 0.5);
      totalScore += cholesterolScore;
      categoryCount++;
    }

    // HDL scoring (0-100)
    if (latestTest.results.cholesterol_hdl) {
      const hdl = latestTest.results.cholesterol_hdl;
      let hdlScore = 100;
      if (hdl < 40) hdlScore = Math.max(0, 50 + hdl);
      totalScore += hdlScore;
      categoryCount++;
    }

    // Hemoglobin scoring (0-100)
    if (latestTest.results.hemoglobin) {
      const hemoglobin = latestTest.results.hemoglobin;
      let hemoglobinScore = 100;
      if (hemoglobin < 12)
        hemoglobinScore = Math.max(0, 100 - (12 - hemoglobin) * 10);
      else if (hemoglobin > 16)
        hemoglobinScore = Math.max(0, 100 - (hemoglobin - 16) * 10);
      totalScore += hemoglobinScore;
      categoryCount++;
    }

    return categoryCount > 0 ? Math.round(totalScore / categoryCount) : 0;
  };

  const score = calculateHealthScore();

  const getScoreColor = (score) => {
    if (score >= 80)
      return {
        color: "text-green-600",
        bg: "from-green-500 to-emerald-500",
        icon: Award,
      };
    if (score >= 60)
      return {
        color: "text-blue-600",
        bg: "from-blue-500 to-indigo-500",
        icon: Shield,
      };
    return {
      color: "text-orange-600",
      bg: "from-orange-500 to-red-500",
      icon: AlertTriangle,
    };
  };

  const scoreStyle = getScoreColor(score);

  const getScoreMessage = (score) => {
    if (score >= 80) return "Excellent! Keep up the great work";
    if (score >= 60) return "Good, with room for improvement";
    return "Consider discussing with your doctor";
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <scoreStyle.icon className={`w-5 h-5 ${scoreStyle.color}`} />
          Health Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center space-y-4">
          <div className={`text-4xl font-bold ${scoreStyle.color}`}>
            {score}
          </div>
          <Progress value={score} className="h-3" />
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              Based on latest blood test results
            </p>
            <div className="text-xs text-slate-500">
              {getScoreMessage(score)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
